/**
 * Passwort zurücksetzen – /reset-password?token=...
 *
 * Design: Terminal-UI, Dark-Theme, Share Tech Mono.
 * Token wird aus dem URL-Query gelesen: /reset-password?token=XYZ
 */

import { navigate } from '../router.js';
import { api } from '../services/api.js';
import { ApiError } from '../services/api.js';

export function renderResetPassword(): void {
  const app = document.getElementById('app');
  if (!app) return;

  // Token aus URL-Query lesen: /reset-password?token=XYZ
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') ?? '';

  app.innerHTML = `
    <main class="auth-page">
      <div class="auth-card">
        <p class="auth-brand">▶ NIGHTTRADE</p>
        <h1 class="auth-title">Neues Passwort vergeben</h1>

        ${
          !token
            ? `<div class="form-error" role="alert">
                Ungültiger oder fehlender Reset-Link.
                <br><a href="/forgot-password">Neuen Link anfordern &#8594;</a>
               </div>`
            : `
        <form id="reset-form" novalidate>
          <div class="form-group">
            <label for="reset-password">Neues Passwort</label>
            <div class="pw-input-wrap">
              <input
                type="password"
                id="reset-password"
                name="password"
                autocomplete="new-password"
                required
                placeholder="Mindestens 12 Zeichen"
              />
              <button type="button" class="pw-toggle-btn" id="reset-pw-toggle" aria-label="Passwort anzeigen">ANZEIGEN</button>
            </div>
            <p class="field-hint">
              Min. 12 Zeichen &middot; Gro&szlig;buchstabe &middot; Zahl &middot; Sonderzeichen
            </p>
          </div>

          <div class="form-group">
            <label for="reset-password-confirm">Passwort bestätigen</label>
            <div class="pw-input-wrap">
              <input
                type="password"
                id="reset-password-confirm"
                name="passwordConfirm"
                autocomplete="new-password"
                required
              />
              <button type="button" class="pw-toggle-btn" id="reset-pw-confirm-toggle" aria-label="Passwort-Bestätigung anzeigen">ANZEIGEN</button>
            </div>
          </div>

          <div id="reset-strength" class="password-strength" hidden>
            <div class="strength-bar">
              <div id="reset-strength-fill" class="strength-fill"></div>
            </div>
            <span id="reset-strength-label" class="strength-label"></span>
          </div>

          <div
            id="reset-error"
            class="form-error"
            role="alert"
            aria-live="polite"
            hidden
          ></div>

          <button type="submit" id="reset-submit" class="primary btn-full">
            Passwort ändern
          </button>
        </form>
            `
        }

        <nav class="auth-links">
          <a href="/login">&#8592; Zurück zum Login</a>
        </nav>
      </div>
    </main>
  `;

  if (!token) return;

  const form = document.getElementById('reset-form') as HTMLFormElement | null;
  const passwordInput = document.getElementById('reset-password') as HTMLInputElement | null;
  const confirmInput = document.getElementById('reset-password-confirm') as HTMLInputElement | null;
  const submitBtn = document.getElementById('reset-submit') as HTMLButtonElement | null;
  const errorDiv = document.getElementById('reset-error') as HTMLDivElement | null;
  const strengthContainer = document.getElementById('reset-strength') as HTMLDivElement | null;
  const strengthFill = document.getElementById('reset-strength-fill') as HTMLDivElement | null;
  // HTMLSpanElement ist strukturell identisch zu HTMLElement (leeres Interface)
  const strengthLabel = document.getElementById('reset-strength-label');

  if (!form || !passwordInput || !confirmInput || !submitBtn || !errorDiv) return;

  // Passwort-Toggles
  const pwToggle = document.getElementById('reset-pw-toggle') as HTMLButtonElement | null;
  const pwConfirmToggle = document.getElementById('reset-pw-confirm-toggle') as HTMLButtonElement | null;
  pwToggle?.addEventListener('click', (): void => {
    const isHidden = passwordInput.type === 'password';
    passwordInput.type = isHidden ? 'text' : 'password';
    pwToggle.textContent = isHidden ? 'VERBERGEN' : 'ANZEIGEN';
  });
  pwConfirmToggle?.addEventListener('click', (): void => {
    const isHidden = confirmInput.type === 'password';
    confirmInput.type = isHidden ? 'text' : 'password';
    pwConfirmToggle.textContent = isHidden ? 'VERBERGEN' : 'ANZEIGEN';
  });

  function showError(message: string): void {
    if (!errorDiv) return;
    errorDiv.textContent = message;
    errorDiv.hidden = false;
  }

  function hideError(): void {
    if (!errorDiv) return;
    errorDiv.hidden = true;
    errorDiv.textContent = '';
  }

  function setLoading(loading: boolean): void {
    if (!submitBtn) return;
    submitBtn.disabled = loading;
    submitBtn.textContent = loading ? '■ WIRD GESPEICHERT...' : 'Passwort ändern';
    submitBtn.setAttribute('aria-busy', String(loading));
  }

  // Client-seitige Passwort-Stärken-Anzeige (nur UX – serverseitige Validierung ist maßgeblich)
  function evaluateStrength(password: string): { score: number; label: string; color: string } {
    let score = 0;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 1) return { score, label: 'SCHWACH', color: 'var(--accent-red)' };
    if (score === 2) return { score, label: 'MITTEL', color: 'var(--accent-orange)' };
    if (score === 3) return { score, label: 'GUT', color: 'var(--accent-gold)' };
    return { score, label: 'STARK', color: 'var(--accent-green)' };
  }

  passwordInput.addEventListener('input', (): void => {
    const password = passwordInput.value;
    if (!password || !strengthContainer || !strengthFill || !strengthLabel) return;

    const { score, label, color } = evaluateStrength(password);
    strengthContainer.hidden = false;
    strengthFill.style.width = `${((score / 4) * 100).toString()}%`;
    strengthFill.style.backgroundColor = color;
    strengthLabel.textContent = label;
    strengthLabel.style.color = color;
  });

  form.addEventListener('submit', (event: Event): void => {
    event.preventDefault();
    hideError();

    const password = passwordInput.value;
    const confirm = confirmInput.value;

    if (!password || !confirm) {
      showError('Bitte beide Felder ausfüllen.');
      return;
    }

    if (password !== confirm) {
      showError('Die Passwörter stimmen nicht überein.');
      return;
    }

    setLoading(true);

    void (async (): Promise<void> => {
      try {
        await api.post<{ message: string }>('/auth/reset-password', { token, password });
        // Bei Erfolg: kurze Meldung, dann Redirect zu Login
        navigate('/login?reset=success');
      } catch (err: unknown) {
        setLoading(false);
        if (err instanceof ApiError) {
          if (err.code === 'RESET_TOKEN_EXPIRED') {
            showError('Dieser Reset-Link ist abgelaufen. Bitte fordere einen neuen an.');
          } else if (err.code === 'INVALID_RESET_TOKEN') {
            showError('Ungültiger Reset-Link. Bitte fordere einen neuen an.');
          } else if (err.code === 'VALIDATION_ERROR') {
            showError('Das Passwort erfüllt nicht die Anforderungen. Min. 12 Zeichen, Großbuchstabe, Zahl, Sonderzeichen.');
          } else {
            showError('Fehler beim Zurücksetzen. Bitte versuche es erneut.');
          }
        } else {
          showError('Verbindungsfehler. Bitte versuche es erneut.');
        }
      }
    })();
  });
}
